import { ShowToast, ToastConfig } from '../model';
import {
  ErrorMonitor,
  ModalLayoutProps,
  PartialLodash,
  Translate,
} from '../services';
import { addEventListener, isHttpError, TypedEmitter } from '../util';

export type EventuallyUpdated = 'submit' | number | Promise<unknown>;

export interface ExecuteParams<A, R> {
  showUndoToast?: boolean;
  showUndoSuccessToast?: boolean;
  submit: (items: A[]) => Promise<R> | undefined;
  eventuallyUpdated?: EventuallyUpdated;
  onEventuallyUpdated?: () => unknown;
  errorToast?: (
    err: unknown,
    params: { retry: () => void },
  ) => ToastConfig | string;
  onTryAgain?: () => void;
  onError?: (err: unknown) => void;
  onUndo?: () => void;
  successToast?: ToastConfig | string;
  onRollback: () => void;
}

interface ExecutionBaseParams<A, R> {
  successToastState: SuccessToastState;
  submit: (actions: A[]) => Promise<R> | undefined;
  eventuallyUpdated?: EventuallyUpdated;
  onEventuallyUpdated?: () => unknown;
  errorToast?: (
    err: unknown,
    params: { retry: () => void },
  ) => ToastConfig | string;

  onTryAgain?: () => void;
  onError?: (err: unknown) => void;
  successToast?: ToastConfig | string;
  onRollback: () => void;
}

interface ExecutionParams<A, R> extends ExecutionBaseParams<A, R> {
  actions: A[];
}

interface ScheduleExecutionParams<A, R> extends ExecutionBaseParams<A, R> {}

interface SuccessToastState {
  undoToast?: UndoToastState;
}

interface UndoToastState {
  timeoutPromises: Promise<{ status?: 'undone' } | undefined>[];
  removeNavigationListener: () => void;
}

export interface CollectionOptimisticExecutorParams {
  readonly showToast: ShowToast;
  readonly translate: Translate;
  readonly errorMonitor: ErrorMonitor;
  readonly batchWait: number;
  readonly lodash: PartialLodash;
  readonly container: {
    events: TypedEmitter<{
      navigation: () => ModalLayoutProps | null | undefined;
    }>;
  };
}

export class CollectionOptimisticExecutor<A> {
  readonly showToast;
  readonly translate;
  readonly errorMonitor;
  readonly lodash;
  readonly container;

  removeToast: (() => void) | null = null;

  _actions: A[] = [];

  scheduleExecution: <R>(params: ScheduleExecutionParams<A, R>) => unknown;

  constructor(params: CollectionOptimisticExecutorParams) {
    this.showToast = params.showToast;
    this.translate = params.translate;
    this.errorMonitor = params.errorMonitor;
    this.lodash = params.lodash;
    this.container = params.container;

    this.executePending = this.executePending.bind(this);

    this.scheduleExecution = params.batchWait
      ? this.lodash.throttle(this.executePending, params.batchWait, {
          leading: false,
          trailing: true,
        })
      : this.executePending;
  }

  removeAndShowToast(toastConfig: ToastConfig) {
    const { removeToast, showToast } = this;
    removeToast?.();
    this.removeToast = showToast(toastConfig).remove;
  }

  rollback(actions: A[], onRollback: () => void) {
    this._actions = this._actions.filter(
      (action) => actions.indexOf(action) === -1,
    );
    onRollback();
  }

  getSuccessToast(
    actions: A[],
    {
      onRollback,
      successToast,
      showUndoToast,
      showUndoSuccessToast,
      onUndo,
    }: {
      successToast?: ToastConfig | string;
      showUndoToast?: boolean;
      showUndoSuccessToast?: boolean;
      onRollback: () => unknown;
      onUndo?: () => unknown;
    },
  ) {
    const { container, showToast, translate: t } = this;

    let resolveUndoTimeoutPromise:
      | ((value: { status: 'undone' }) => void)
      | null = null;

    let resolveDismissPromise: (() => void) | null = null;

    const state: SuccessToastState = {};

    if (showUndoToast) {
      const dismissPromise = new Promise<undefined>((resolve) => {
        resolveDismissPromise = () => resolve(undefined);
      });

      state.undoToast = {
        timeoutPromises: [
          new Promise<{ status: 'undone' }>((_resolve) => {
            resolveUndoTimeoutPromise = _resolve;
          }),
          new Promise<{ status?: undefined } | undefined>((resolve) =>
            setTimeout(resolve, 6000),
          ),
          dismissPromise,
        ],
        removeNavigationListener: addEventListener(
          container.events,
          'navigation',
          () => ({
            title: t('cairo.dragAndDrop.leaveSite.modal.title'),
          }),
        ),
      };
    }

    if (successToast) {
      const toastConfig =
        typeof successToast === 'string'
          ? { message: successToast, biName: `cairo-undo` }
          : successToast;
      const userOnCloseClick = toastConfig.onCloseClick;

      this.removeAndShowToast({
        action: showUndoToast
          ? {
              text: t('cairo.toast.undo'),
              uiType: 'LINK',
              onClick: () => {
                resolveUndoTimeoutPromise?.({ status: 'undone' });
                this.rollback(actions, onRollback);
                onUndo?.();
                if (showUndoSuccessToast) {
                  showToast({
                    type: 'STANDARD',
                    priority: 'LOW',
                    biName: `cairo-undone`,
                    message: t('cairo.toast.undone'),
                  });
                }
              },
              removeToastOnClick: true,
            }
          : undefined,
        ...toastConfig,
        type: 'SUCCESS',
        onCloseClick: () => {
          userOnCloseClick?.();
          resolveDismissPromise?.();
        },
      });
    }

    return state;
  }

  async _execute<R>(params: ExecutionParams<A, R>) {
    const {
      successToastState,
      submit,
      errorToast: errorToastFn,
      onTryAgain,
      onError,
      onRollback,
      eventuallyUpdated,
      onEventuallyUpdated,
      actions,
    } = params;
    const { errorMonitor, translate: t } = this;

    const { undoToast } = successToastState;

    try {
      if (undoToast) {
        const undoResult = await Promise.race(undoToast.timeoutPromises);
        undoToast.removeNavigationListener();
        if (undoResult?.status === 'undone') {
          return undoResult;
        }
      }

      const submitPromise = await submit(actions);

      const updateServerAfterPromise =
        typeof eventuallyUpdated === 'number'
          ? await Promise.all([
              submitPromise,
              new Promise((resolve) => setTimeout(resolve, eventuallyUpdated)),
            ]).then(() => submitPromise)
          : submitPromise;

      const result = await updateServerAfterPromise;
      await onEventuallyUpdated?.();

      return {
        result,
        status: 'success',
      };
    } catch (err) {
      console.error(err);
      errorMonitor.captureException(err);
      this.rollback(actions, onRollback);
      onError?.(err);

      if (errorToastFn) {
        const onRetryClick = () => {
          onTryAgain?.();
          this.removeToast?.();

          this.execute(actions, params);
        };

        const errorToast = errorToastFn(err, { retry: onRetryClick });

        const requestId = isHttpError(err) ? err.requestId : undefined;

        const toastConfig: ToastConfig = {
          action: {
            uiType: 'LINK',
            text: t('cairo.toast.retry'),
            onClick: onRetryClick,
            removeToastOnClick: true,
          },
          ...(typeof errorToast === 'string'
            ? { message: errorToast, biName: `cairo-error-toast` }
            : errorToast),
          type: 'ERROR',
          details: requestId ? { requestId } : undefined,
        };

        if (toastConfig.action) {
          toastConfig.action.uiType = 'LINK';
          toastConfig.action.removeToastOnClick = true;
        }

        this.removeAndShowToast(toastConfig);
      }

      return { status: 'error', err };
    }
  }

  async executePending<R>(params: ScheduleExecutionParams<A, R>) {
    const { _actions: actions } = this;

    this._actions = [];

    return this._execute({
      ...params,
      actions,
    });
  }

  execute<R>(actions: A[], params: ExecuteParams<A, R>) {
    const {
      showUndoToast,
      showUndoSuccessToast,
      successToast,
      onRollback,
      onUndo,
    } = params;

    this._actions = [...this._actions, ...actions];

    const successToastState = this.getSuccessToast(actions, {
      onRollback,
      successToast,
      showUndoToast,
      showUndoSuccessToast,
      onUndo,
    });

    this.scheduleExecution({
      ...params,
      successToastState,
    });
  }
}
